import { useEffect, useMemo, useState } from 'react';

/**
 * Which practicals a learner has actually finished.
 *
 * A practical opens in a tab of its own, so the lesson behind it has no
 * way of watching the work happen. Each lab writes a flag here the
 * moment it reaches its last screen - the same moment it shows "Back to
 * the lesson" - and the lesson tab hears about it through the `storage`
 * event, which fires in every *other* tab of the same origin. That is
 * precisely the tab that is waiting, so the Next button unlocks while
 * the child is still reading the ending.
 *
 * Why localStorage and not the server:
 *
 *   - every lab already keeps its working state there, so a flag beside
 *     it needs no new endpoint, no new table and nothing to run on
 *     production;
 *   - the gate it sits next to is harsher already. The in-lesson
 *     activities are held in React state alone, so a reload makes a
 *     child redo them; this survives reloads, closed tabs and
 *     restarts.
 *
 * The cost is that a flag does not follow a child to another browser or
 * another machine. Doing the practical again over there is the price,
 * and the practical is the point.
 */

const PREFIX = 'cybercub.done.';

/** `/lab/encodings` -> `cybercub.done.lab.encodings` */
export const doneKey = (path) =>
  PREFIX + String(path).replace(/^\/+/, '').replace(/\//g, '.');

/**
 * Whether this browser will keep a flag at all.
 *
 * A private window, or a browser told to block site data, throws on
 * write. Without this check the flag would read back as "not finished"
 * for ever and the lesson would be impassable - which is a far worse
 * outcome than a skipped practical, so callers fall back to something
 * softer when this is false.
 */
export const canRemember = (() => {
  try {
    const probe = `${PREFIX}probe`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
})();

/** Has this one been finished? Anything without a path counts as done. */
export function isDone(path) {
  if (!path) return true;
  try {
    return localStorage.getItem(doneKey(path)) !== null;
  } catch {
    return false;
  }
}

const readAll = (paths) => Object.fromEntries(paths.map((p) => [p, isDone(p)]));

/**
 * Live "finished?" answers for a set of practicals.
 *
 * Re-reads on three signals: a `storage` event (another tab wrote a
 * flag), the window regaining focus, and the tab becoming visible. The
 * last two are belt and braces for browsers that drop the event, and
 * they cost nothing - the state object is only replaced when an answer
 * has genuinely changed, so a focus event on an unchanged lesson does
 * not re-render anything.
 *
 * Pass a memoised array: the hook re-subscribes when the list changes.
 */
export function usePracticals(paths) {
  const list = useMemo(() => paths ?? [], [paths]);
  const [done, setDone] = useState(() => readAll(list));

  useEffect(() => {
    const recheck = () =>
      setDone((prev) => {
        const next = readAll(list);
        const same = list.every((p) => prev[p] === next[p]);
        return same ? prev : next;
      });

    recheck();

    // A null key means the whole store was cleared, which counts.
    const onStorage = (e) => {
      if (!e.key || e.key.startsWith(PREFIX)) recheck();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [list]);

  return done;
}

/**
 * Called by a lab: records that this practical is finished.
 *
 * Written once and never rewritten, so the timestamp stays the first
 * time it was earned and the `storage` event fires exactly once.
 */
export function useFinished(path, finished) {
  useEffect(() => {
    if (!finished || !path) return;
    try {
      const key = doneKey(path);
      if (localStorage.getItem(key) === null) {
        localStorage.setItem(key, new Date().toISOString());
      }
    } catch {
      /* private window - the lesson falls back to a softer gate */
    }
  }, [path, finished]);
}
